// Typed wrapper around /api/v1/admin/invite-codes for the User Management hub.
// Every call targets an explicit cluster_id (the hub lets PLATFORM/DEVELOPER
// pick a cluster; CLUSTER admins get their own).

import type {
  AddressScheme,
  CreateInviteCodeRequest,
  ListInviteCodesResponse,
  PublicInviteCode,
} from '@livinhome/shared';
import { api } from './api-client';

export type { PublicInviteCode, AddressScheme };

export interface UnlinkedUnit {
  id: string;
  block: string;
  number: string;
  label: string;
}

export async function listInviteCodes(
  clusterId: string,
  q: { unit_id?: string; status?: string; cursor?: string; limit?: number } = {},
): Promise<ListInviteCodesResponse> {
  const { data } = await api.get<ListInviteCodesResponse>('/admin/invite-codes', {
    params: { cluster_id: clusterId, ...q },
  });
  return data;
}

export async function listUnlinkedUnits(clusterId: string): Promise<UnlinkedUnit[]> {
  const { data } = await api.get<{ units: UnlinkedUnit[] }>('/admin/invite-codes/unlinked-units', {
    params: { cluster_id: clusterId },
  });
  return data.units;
}

// The cluster's unit-address scheme — which parts the create dialog should render.
export async function getInviteCodeScheme(clusterId: string): Promise<AddressScheme> {
  const { data } = await api.get<AddressScheme>('/admin/invite-codes/scheme', {
    params: { cluster_id: clusterId },
  });
  return data;
}

// max_uses is optional on the wire — the server defaults it (MAX_RESIDENTS_PER_UNIT).
export type CreateInviteCodeBody = Omit<CreateInviteCodeRequest, 'max_uses'> & { max_uses?: number };

export async function createInviteCode(
  clusterId: string,
  body: CreateInviteCodeBody,
): Promise<PublicInviteCode> {
  const { data } = await api.post<{ code: PublicInviteCode }>('/admin/invite-codes', body, {
    params: { cluster_id: clusterId },
  });
  return data.code;
}

export async function deleteInviteCode(clusterId: string, id: string): Promise<void> {
  await api.delete(`/admin/invite-codes/${id}`, { params: { cluster_id: clusterId } });
}

// ─── Display helpers ───────────────────────────────────────────────────────

export function formatUnitLabel(code: PublicInviteCode): string {
  // unit_label is the server-derived, scheme-aware address (e.g. "A2 15",
  // "Jl. Mawar 52B 16"), so prefer it; fall back to the raw number.
  return code.unit_label || code.unit_number || '—';
}

// Remaining time until a code expires, as "Xh Ym" (e.g. "13h 45m"). Computed at
// render time, so it refreshes whenever the codes list reloads.
export function expiresIn(expiresAt: string | null): string {
  if (!expiresAt) return '—';
  const t = new Date(expiresAt).getTime();
  if (Number.isNaN(t)) return '—'; // guard legacy/garbage values (e.g. the string "null")
  const ms = t - Date.now();
  if (ms <= 0) return 'Expired';
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export function statusLabel(status: PublicInviteCode['status']): string {
  switch (status) {
    case 'ACTIVE':
      return 'Active';
    case 'EXHAUSTED':
      return 'Exhausted';
    case 'EXPIRED':
      return 'Expired';
    case 'REVOKED':
      return 'Revoked';
  }
}
